import { z } from 'zod';
import { ESessionMode } from '../types/enums/session-mode';

/**
 * Validación de la asignación de un entrenamiento a una sesión.
 *
 * #36 (reunión 08/08 con Enzo): "al asignar entrenamiento virtual sincrónico,
 * híbrido y presencial que sea todo opcional nomás fue". Antes `handleAssign`
 * bloqueaba con tres if/else sueltos: ubicación en presencial/híbrido, método de
 * contacto en virtual sincrónico/híbrido y hora en presencial/virtual sincrónico.
 * Los tres se sacaron: el coach puede asignar y completar esos datos después.
 *
 * Queda como obligatorio solo lo que la sesión no puede resolver sola: qué
 * entrenamiento se asigna y en qué modalidad.
 */
export const assignWorkoutToSessionSchema = z.object({
  selectedWorkout: z
    .union([z.number(), z.string()])
    .nullable()
    .refine((v) => v !== null && v !== '', {
      message: 'assignWorkout.error.workoutRequired'
    }),
  sessionMode: z
    .enum([
      ESessionMode.PRESENTIAL,
      ESessionMode.VIRTUAL_SYNCHRONOUS,
      ESessionMode.VIRTUAL_ASYNCHRONOUS,
      ESessionMode.HYBRID
    ])
    .nullable()
    .refine((v) => v !== null, {
      message: 'assignWorkout.error.sessionModeRequired'
    }),
  // Opcionales en todas las modalidades (#36).
  location: z.string().optional().nullable(),
  contactMethod: z.string().optional().nullable(),
  sessionTime: z.date().optional().nullable(),
  meetingLink: z.string().optional().nullable()
});

export type AssignWorkoutToSessionInput = z.input<typeof assignWorkoutToSessionSchema>;

/**
 * Devuelve `{ isValid, errors }` siguiendo la convención del repo: los errores
 * son claves de i18n para que el caller las traduzca con `intl.formatMessage`.
 */
export function validateAssignWorkoutToSession(input: AssignWorkoutToSessionInput): {
  isValid: boolean;
  errors: Record<string, string>;
} {
  const result = assignWorkoutToSessionSchema.safeParse(input);

  if (result.success) return { isValid: true, errors: {} };

  const errors: Record<string, string> = {};
  result.error.issues.forEach((issue) => {
    const field = String(issue.path[0] ?? 'form');
    if (!errors[field]) errors[field] = issue.message;
  });

  return { isValid: false, errors };
}
